import { makeStyles, Spinner } from "@fluentui/react-components";
import {
  AirplaneLandingFilled,
  AirplaneTakeOffFilled,
} from "@fluentui/react-icons";
import { useContext } from "react";
import { StatusIconSelector } from "../../../components-v2/common/icon-selectors/StatusIconSelector";
import { LayoutContext } from "../../../context/LayoutContext";
import { $cloud90, $sky60, $white } from "../../../cssjs/colors";
import { FlightCardTypes } from "../../../enum/flights/FlightEnum";
import { FlightKPIUrlsAndFilterHoursElement } from "../../../enum/FlightsEnum";
import { GlobalFilterOptionsEnum } from "../../../enum/global-filter/GlobalFilterEnum";
import { useApiCall } from "../../../hooks/useApiCall";
import { CommonHelper } from "../../../utils/common/CommonHelper";
import { EMPTY_STATE_MESSAGE } from "../../../utils/common/Constants";
import { formatRelativeTime } from "../../../utils/common/DateHelperFunctions";
import { flightTableViewTypes } from "../../../utils/flights/FlightConstants";
import { FlightSummaryHelper } from "../../../utils/flights/FlightSummaryHelper";
import { NoDataCard } from "../../common/no-data-card/NoDataCard";
import { FlightKpiCardHeader } from "./FlightKpiCardHeader";

const defaultBuffer = {
  departureEarlyBuffer: "0 min",
  departureLateBuffer: "0 min",
  arrivalEarlyBuffer: "0 min",
  arrivalLateBuffer: "0 min",
  overallBuffer: "0 min",
};

interface IFlightKPICardsProps {
  selectedSection: FlightCardTypes;
  onSectionChange: (newSection: FlightCardTypes) => void;
  airport: string;
  kpiData?: any[] | undefined;
  cardName: FlightCardTypes;
  showSettings?: boolean;
  showInformation?: boolean;
  kpiConfigData: FlightKPIUrlsAndFilterHoursElement | undefined;
  hopSettingsDataList: any[];
}

const FlightKPICards: React.FC<IFlightKPICardsProps> = ({
  selectedSection,
  onSectionChange,
  airport,
  kpiData,
  cardName,
  showSettings,
  showInformation,
  kpiConfigData,
  hopSettingsDataList,
}) => {
  const { selectedFilter, currentTime } = useContext(LayoutContext);

  const {
    data: apiData,
    error,
    isLoading,
  } = useApiCall<any[]>({
    url: kpiConfigData?.url || "",
    dependencies: [airport, kpiConfigData?.url],
  });

  const styles = useStyles();
  const { label } = flightTableViewTypes[cardName] || { label: "" };
  const sourceData = kpiData || apiData || [];
  const overall = sourceData?.find(
    (resource: any) => resource.type === "Overall"
  );
  const data =
    label === "Regulated Flight"
      ? sourceData.filter((resource: any) => resource.type !== "Overall")
      : sourceData;

  const isRolling = selectedFilter === GlobalFilterOptionsEnum.rolling;
  const isToday = selectedFilter === GlobalFilterOptionsEnum.today;

  const startDateText = formatRelativeTime(
    kpiConfigData?.filterDate.startDate,
    currentTime
  );
  const endDateText = formatRelativeTime(
    kpiConfigData?.filterDate.endDate,
    currentTime
  );

  const kpiCalulcationTextContent =
    isRolling || isToday ? (
      <>
        This KPI is calculated from <i>{startDateText}</i> to{" "}
        <i>{endDateText}</i>.
      </>
    ) : (
      ""
    );

  const Buffers = FlightSummaryHelper.getBufferTexts(hopSettingsDataList);
  const cardBufferType = FlightSummaryHelper.getCardBuffer(cardName);
  const cardBuffer = cardBufferType ? Buffers[cardBufferType] : defaultBuffer;

  const handleCardClick = () => {
    if (cardName) {
      onSectionChange(cardName);
    }
  };

  const renderBreakdown = (breakdown: any[]) =>
    breakdown.map((item: any) => {
      const showPadding = cardName !== FlightCardTypes.TURNAROUND_SUFFICIENCY;
      return (
        <div
          key={CommonHelper.getGuid()}
          className={`d-flex flex-1${showPadding ? " pl-1" : ""}`}
        >
          <StatusIconSelector status={item.status} class={`${styles.icon}`} />
          <span className="fs-16">{Math.round(item.kpiValue)}</span>
        </div>
      );
    });

  const renderDataRows = () =>
    data.map((resource: any) => (
      <div
        key={CommonHelper.getGuid()}
        className="d-flex"
        style={{ lineHeight: "1.5" }}
      >
        <div className="fs-14 d-flex" style={{ minWidth: "150px" }}>
          {resource.type}
          {resource.type !== "Overall" &&
            cardName !== FlightCardTypes.REGULATED_FLIGHT && (
              <div className={`${styles.fontcolor} ml-1`}>
                {resource.type.toLowerCase() === "arrivals" ? (
                  <AirplaneLandingFilled />
                ) : resource.type.toLowerCase() === "departures" ? (
                  <AirplaneTakeOffFilled />
                ) : null}
              </div>
            )}
        </div>
        {resource.breakdown?.length > 0 && renderBreakdown(resource.breakdown)}
        {cardName !== FlightCardTypes.TURNAROUND_SUFFICIENCY &&
          cardName !== FlightCardTypes.ADDITIONAL_DELAY && (
            <div className="d-flex flex-1 pl-1">
              <StatusIconSelector
                status={resource.status}
                class={`${styles.icon}`}
              />
              <span className="fs-16">{Math.round(resource.kpiValue)}</span>
            </div>
          )}
      </div>
    ));

  const renderContent = () => {
    if (error) {
      return <NoDataCard message={EMPTY_STATE_MESSAGE.Data} />;
    }

    if (isLoading) {
      return (
        <Spinner
          labelPosition="below"
          label="Loading..."
          className={styles.cardLoader}
        />
      );
    }

    if (data.length > 0) {
      return (
        <div
          className={`align-items-center ${styles.cardPerformanceFactorItemContainer}`}
        >
          <div className={`d-flex fw-500 mt-1 ${styles.fontcolor}`}>
            <div className="fs-16" style={{ minWidth: "135px" }}></div>
            {data[0]?.breakdown?.map((item: any) => (
              <div key={CommonHelper.getGuid()} className="flex-1">
                {item.type}
                {cardName !== FlightCardTypes.ADDITIONAL_DELAY &&
                  cardName !== FlightCardTypes.TURNAROUND_SUFFICIENCY &&
                  cardName !== FlightCardTypes.REGULATED_FLIGHT && (
                    <span>(%)</span>
                  )}
              </div>
            ))}
            {cardName !== FlightCardTypes.TURNAROUND_SUFFICIENCY &&
            cardName !== FlightCardTypes.ADDITIONAL_DELAY ? (
              <div className="flex-1">
                Summary
                {cardName !== FlightCardTypes.REGULATED_FLIGHT && (
                  <span>(%)</span>
                )}
              </div>
            ) : null}
          </div>
          <div className="mt-1">{renderDataRows()}</div>
        </div>
      );
    }

    return <NoDataCard message={EMPTY_STATE_MESSAGE.Data} />;
  };

  return (
    <div
      className={`${styles.cardContainer} amd-container-vcolumn`}
      data-testid="flight360-kpis-card"
      style={{
        border: `2px solid ${selectedSection === cardName ? $sky60 : "#ccc"}`,
      }}
      onClick={handleCardClick}
    >
      <FlightKpiCardHeader
        label={label}
        cardName={cardName}
        overall={overall}
        showSettings={showSettings}
        showInformation={showInformation}
        hasError={Boolean(error)}
        airport={airport}
        cardBuffer={cardBuffer}
        kpiCalulcationTextContent={kpiCalulcationTextContent}
      />
      {renderContent()}
    </div>
  );
};

export default FlightKPICards;

const useStyles = makeStyles({
  cardLoader: {
    height: "100%",
    width: "300px",
    margin: "auto",
  },
  icon: {
    fontSize: "16px !important",
    strokeWidth: "0.5px !important",
    paddingRight: "2px !important",
    paddingTop: "5px !important",
    verticalAlign: "bottom !important",
  },
  cardPerformanceFactorItemContainer: {
    color: $cloud90,
    paddingLeft: "16px",
    borderRadius: "8px",
  },
  fontcolor: {
    color: $sky60,
  },
  cardContainer: {
    padding: "10px 20px 0px 20px",
    width: "95%",
    height: "146px",
    display: "flex",
    boxSizing: "border-box",
    border: `2px solid #ccc`,
    borderRadius: "8px",
    backgroundColor: $white,
    marginBottom: "7px",
  },
});
